import { Button, Card, SvgIcon } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine, RiLoader3Fill } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { SimpleSkeleton } from '../../../components/SkeletonElements';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { Operator } from '../../../types/operator';
import { Role, User } from '../../../types/user';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';
import UserStatusBadge from './UserStatusBadge';

const UsersPage = () => {
  const { t } = useLanguage();
  const { getUsers, getOperators } = useIotsApi();

  const {
    data: users = [],
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const {
    data: operatorsMap = {},
    isFetching: isFetchingOperators,
    isLoading: isLoadingOperators,
    error: operatorsError,
  } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
    select: operators =>
      operators.reduce(
        (acc, operator) => ({ ...acc, [operator.id]: operator }),
        {} as Record<string, Operator>,
      ),
  });

  useQueryErrorNotifier([error, operatorsError]);

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const columns = useMemo<GridColDef<User>[]>(
    () => [
      {
        field: 'firstName',
        flex: 3,
        headerName: t('content.first-name'),
        minWidth: 160,
      },
      {
        field: 'lastName',
        flex: 3,
        minWidth: 160,
        headerName: t('content.last-name'),
      },
      {
        field: 'email',
        flex: 3,
        minWidth: 250,
        headerName: t('content.email-address'),
      },
      {
        field: 'operatorId',
        flex: 3,
        minWidth: 200,
        headerName: t('content.operator'),
        valueGetter: (operatorId: string) =>
          operatorsMap[operatorId]?.name ?? '',
        renderCell: ({ row }) => (
          <OperatorCellContent
            operatorId={row.operatorId}
            operatorsMap={operatorsMap}
            invalidOperatorText={t('content.invalid-operator')}
            isLoading={isLoadingOperators}
            isFetching={isFetchingOperators}
          />
        ),
      },
      {
        type: 'singleSelect',
        field: 'role',
        flex: 3,
        minWidth: 160,
        headerName: t('content.role'),
        valueOptions: [
          { value: Role.OPERATOR, label: t(`role.${Role.OPERATOR}`) },
          { value: Role.DRCD, label: t(`role.${Role.DRCD}`) },
          { value: Role.ADMIN, label: t(`role.${Role.ADMIN}`) },
        ],
      },
      {
        field: 'isActive',
        display: 'flex',
        minWidth: 140,
        headerName: t('content.status'),
        type: 'singleSelect',
        valueOptions: [
          { value: true, label: t('content.active') },
          { value: false, label: t('content.inactive') },
        ],
        renderCell: params => (
          <UserStatusBadge isActive={params.value as boolean} small />
        ),
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: ({ row: { id } }) => (
          <AdminTableDetailLink
            isLoading={isFetching}
            href={`/admin/users/${id}`}
          />
        ),
      },
    ],
    [t, isFetching, operatorsMap, isLoadingOperators, isFetchingOperators],
  );

  return (
    <MainSectionTemplate
      title={t('content.users')}
      actions={
        <Link to="/admin/users/create">
          <Button
            variant="contained"
            startIcon={<SvgIcon component={RiAddLine} />}
            size="small"
          >
            {t('content.add-new')}
          </Button>
        </Link>
      }
    >
      <Card variant="outlined">
        <CustomDataGrid
          rows={users}
          loading={isLoading}
          columns={columns}
          onFilterModelChange={onFilterModelChange}
          initialState={{
            filter: {
              filterModel: initialFilterModel,
            },
            sorting: {
              sortModel: [
                {
                  field: 'firstName',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
      </Card>
    </MainSectionTemplate>
  );
};

interface OperatorCellContentProps {
  operatorId?: string;
  isLoading: boolean;
  isFetching: boolean;
  invalidOperatorText: string;
  operatorsMap: Record<string, Operator>;
}

const OperatorCellContent = ({
  invalidOperatorText,
  isFetching,
  isLoading,
  operatorId,
  operatorsMap,
}: OperatorCellContentProps) => {
  const { t } = useLanguage();

  if (!operatorId) {
    return null;
  }
  if (isLoading) {
    return <SimpleSkeleton />;
  }

  const operator = operatorsMap[operatorId];

  const textContent = !operator ? (
    <span className="line-through">{invalidOperatorText}</span>
  ) : (
    <span>
      {operator.name}
      {operator.isActive ? null : <i>&nbsp;({t('content.inactive')})</i>}
    </span>
  );

  return (
    <span className="inline-flex items-center gap-2">
      {textContent}
      {isFetching ?? <RiLoader3Fill className="animate-spin" />}
    </span>
  );
};

export default UsersPage;
